import { useFetcher } from 'react-router';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button as RoccoButton } from '@rocco/components/button';
import { Input as RoccoInput } from '@rocco/components/input';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { REQUEST_EMAIL_LOGIN_CODE_API_ROUTE } from '~/routes/remix-api.user.request-email-login-code';
import { VERIFY_EMAIL_LOGIN_CODE_API_ROUTE } from '~/routes/remix-api.user.verify-email-login-code';
import { cn } from '@rocco/utils/cn';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';

const SIX_DIGIT_CODE_MESSAGE = 'Enter the 6-digit code from your email';

const CODE_LENGTH = 6;

/** Mirrors code_request_window_seconds in app/_config/email-code-login.yml. */
const RESEND_COOLDOWN_SECONDS = 60;

const formatCountdown = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

interface OtpInputProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    hasError?: boolean;
}

/** Six individual digit boxes with auto-advance, backspace navigation and full-code paste. */
export function OtpInput({
    value,
    onChange,
    disabled,
    hasError,
}: OtpInputProps) {
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const focusBox = (index: number) => {
        const bounded = Math.min(Math.max(index, 0), CODE_LENGTH - 1);
        inputRefs.current[bounded]?.focus();
    };

    const handleBoxInput = (index: number, raw: string) => {
        const digits = raw.replace(/\D/g, '');
        if (!digits) {
            onChange(value.slice(0, index));
            return;
        }
        const next = (value.slice(0, index) + digits).slice(0, CODE_LENGTH);
        onChange(next);
        focusBox(next.length < CODE_LENGTH ? next.length : CODE_LENGTH - 1);
    };

    const handleBoxKeyDown = (
        index: number,
        e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (e.key === 'Backspace' && !value[index]) {
            e.preventDefault();
            onChange(value.slice(0, Math.max(index - 1, 0)));
            focusBox(index - 1);
        }
        if (e.key === 'ArrowLeft') focusBox(index - 1);
        if (e.key === 'ArrowRight') focusBox(index + 1);
    };

    return (
        // The boxes are fluid up to their design width. At their fixed width
        // the row came to 345px against the 327px a 375px phone leaves inside
        // the sheet, and overflowed with nowhere to scroll; with room they hit
        // the cap and render exactly as designed.
        <div className="flex items-center justify-center gap-2.25">
            {Array.from({ length: CODE_LENGTH }, (_, index) => {
                const digit = value[index] ?? '';
                return (
                    <input
                        key={index}
                        ref={(el) => {
                            inputRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        value={digit}
                        disabled={disabled}
                        onChange={(e) => handleBoxInput(index, e.target.value)}
                        onKeyDown={(e) => handleBoxKeyDown(index, e)}
                        onFocus={(e) => e.target.select()}
                        aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
                        className={cn(
                            // 16px floor, as every gate field carries — see
                            // GATE_FIELD_INPUT_CLASS. A zoom here is the worst
                            // of them: it fires on the numeric keyboard, on the
                            // one step where six boxes already fill the width.
                            'h-14.5 w-full max-w-12.5 min-w-0 flex-1 rounded-[0.625rem] bg-[#fbfbf9] text-center text-[max(1.375rem,16px)] font-semibold text-[#1a1a1a] focus:outline-none md:text-[1.375rem]',
                            hasError
                                ? 'border-[1.5px] border-destructive'
                                : digit
                                  ? 'border-[1.5px] border-[#1a1a1a]'
                                  : 'border border-[#dcdbd4] focus:border-[#1a1a1a]',
                            disabled && 'opacity-50',
                        )}
                    />
                );
            })}
        </div>
    );
}

/**
 * Login modal primary submit — matches aria `Button` (`color="dark"`, `size={18}`, `fluid`):
 * charcoal fill, square corners, 18px label (not Rocco pill `color="dark"`).
 */
const loginModalSubmitButtonClass =
    'w-full! rounded-none! border-0! bg-[#343434]! px-4 py-4 text-lg! font-normal! leading-none text-white! hover:bg-[#4a4a4a]! hover:text-white!';

const SUBMIT_LABEL_FADE_MS = 300;
const RESEND_ACK_DISPLAY_MS = 1000;

/** Dedupes Strict Mode remount while a send is in flight (fetcher persists across remounts). */
const autoSendInFlightByEmail = new Set<string>();

interface LoginModalSubmitButtonProps {
    type: 'submit' | 'button';
    children: ReactNode;
    isLoading?: boolean;
    disabled?: boolean;
    labelVisible?: boolean;
    onClick?: () => void;
}

/** White circle + compact dark tick — visible on the charcoal submit button. */
function CodeSentCheckIcon() {
    return (
        <span
            aria-hidden
            className="inline-flex size-[1em] shrink-0 items-center justify-center rounded-full bg-white text-[#343434]"
        >
            <svg
                viewBox="0 0 16 16"
                className="size-[0.55em] shrink-0"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M4.25 8.25L7 11L11.75 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    );
}

function LoginModalSubmitButton({
    type,
    children,
    isLoading,
    disabled = false,
    labelVisible = true,
    onClick,
}: LoginModalSubmitButtonProps) {
    return (
        <RoccoButton
            type={type}
            color="grayDark"
            shape="square"
            weight="normal"
            textSize="large"
            className={loginModalSubmitButtonClass}
            isLoading={isLoading}
            disabled={disabled || isLoading}
            onClick={onClick}
        >
            <span
                className={`inline-flex items-center justify-center gap-2 transition-opacity duration-300 ${
                    labelVisible ? 'opacity-100' : 'opacity-0'
                }`}
            >
                {children}
            </span>
        </RoccoButton>
    );
}

/** Matches server normalisation: strip spaces/dashes, then require exactly six digits. */
const isSixDigitLoginCode = (value: string): boolean =>
    /^\d{6}$/.test(value.replace(/[\s-]/g, ''));

const codeSchema = z.object({
    code: z.string().trim().refine(isSixDigitLoginCode, {
        message: SIX_DIGIT_CODE_MESSAGE,
    }),
});

type CodeForm = z.infer<typeof codeSchema>;

type EmailCodeFetcherData = {
    ok?: boolean;
    message?: string;
    /** Present on a successful verify; see the verify-email-login-code action. */
    trackedGuestID?: number;
};

interface EmailCodeEntryStepProps {
    email: string;
    onBack: () => void;
    /**
     * @param trackedGuestID the verified member's tracked-guest id, when the
     *     action could report it — carried so the auth event names the guest
     *     without waiting for a loader revalidation.
     */
    onSuccess: (trackedGuestID?: number) => void;
    /** When true, request a code as soon as the step mounts. */
    autoSend?: boolean;
    /** Primary submit label — matches login form (e.g. "Login"). */
    submitLabel?: string;
    /** When true, back is rendered in the modal header instead of inline. */
    hideBack?: boolean;
    /** Modal header chrome (back visibility) while on this step. */
    onChromeStateChange?: (state: EmailCodeStepChromeState) => void;
    /** legacy = old modal styling; rocco = revamped auth modal (OTP boxes, countdown, pill CTA). */
    variant?: 'legacy' | 'rocco';
}

export interface EmailCodeStepChromeState {
    sendingCode: boolean;
    requestSent: boolean;
    requestError: string | null;
    autoSend: boolean;
}

export function EmailCodeEntryStep({
    email,
    onBack,
    onSuccess,
    autoSend = false,
    submitLabel = 'Login',
    hideBack = false,
    onChromeStateChange,
    variant = 'legacy',
}: EmailCodeEntryStepProps) {
    const normalizedEmail = email.trim().toLowerCase();
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const verifyFetcher = useFetcher<EmailCodeFetcherData>();
    const requestFetcher = useFetcher<EmailCodeFetcherData>();
    const [requestSent, setRequestSent] = useState(false);
    const [requestError, setRequestError] = useState<string | null>(null);
    const [submitLabelPhase, setSubmitLabelPhase] = useState<
        'login' | 'code-sent'
    >('login');
    const [submitLabelVisible, setSubmitLabelVisible] = useState(true);
    const [resendSending, setResendSending] = useState(false);
    const handledRequestFetcherKeyRef = useRef<string | null>(null);
    const handledVerifyFetcherKeyRef = useRef<string | null>(null);
    /** Ignore stale fetcher.data until sendCode() runs in this mount/session. */
    const awaitingRequestResponseRef = useRef(false);
    /** Ensures autoSend only triggers one request per step visit (not on fetcher idle). */
    const autoSendInitiatedRef = useRef(false);
    const resendAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const sendingCode = requestFetcher.state !== 'idle';
    /** Epoch ms when resending becomes available again; drives the countdown. */
    const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(
        null,
    );
    const [resendRemaining, setResendRemaining] = useState(0);

    useEffect(() => {
        if (resendAvailableAt === null) {
            return;
        }
        const update = () =>
            setResendRemaining(
                Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000)),
            );
        update();
        const intervalId = setInterval(update, 1000);
        return () => clearInterval(intervalId);
    }, [resendAvailableAt]);

    useLayoutEffect(() => {
        if (requestFetcher.state !== 'idle') {
            awaitingRequestResponseRef.current = true;
        }
    }, [requestFetcher.state]);

    useEffect(() => {
        onChromeStateChange?.({
            sendingCode,
            requestSent,
            requestError,
            autoSend,
        });
    }, [autoSend, onChromeStateChange, requestError, requestSent, sendingCode]);

    const form = useForm<CodeForm>({
        resolver: zodResolver(codeSchema),
        defaultValues: { code: '' },
    });

    const sendCode = useCallback(() => {
        if (!normalizedEmail) {
            setRequestError('Enter your email address first.');
            return;
        }

        if (
            requestFetcher.state !== 'idle' ||
            awaitingRequestResponseRef.current
        ) {
            return;
        }

        setRequestError(null);
        form.clearErrors('code');
        handledRequestFetcherKeyRef.current = null;
        awaitingRequestResponseRef.current = true;
        autoSendInFlightByEmail.add(normalizedEmail);
        requestFetcher.submit(
            { email: normalizedEmail },
            { method: 'post', action: REQUEST_EMAIL_LOGIN_CODE_API_ROUTE },
        );
    }, [form, normalizedEmail, requestFetcher]);

    const clearResendAckTimeout = useCallback(() => {
        if (resendAckTimeoutRef.current !== null) {
            clearTimeout(resendAckTimeoutRef.current);
            resendAckTimeoutRef.current = null;
        }
    }, []);

    const handleResendCode = useCallback(() => {
        clearResendAckTimeout();
        setSubmitLabelPhase('login');
        setSubmitLabelVisible(true);
        form.resetField('code', { defaultValue: '' });
        if (verifyFetcher.data) {
            handledVerifyFetcherKeyRef.current = `${verifyFetcher.state}:${JSON.stringify(verifyFetcher.data)}`;
        }
        if (!normalizedEmail) {
            setRequestError('Enter your email address first.');
            return;
        }
        setResendSending(true);
        sendCode();
    }, [
        clearResendAckTimeout,
        form,
        normalizedEmail,
        sendCode,
        verifyFetcher.data,
        verifyFetcher.state,
    ]);

    const showResendAckOnSubmitButton = useCallback(() => {
        clearResendAckTimeout();
        setSubmitLabelPhase('code-sent');
        setSubmitLabelVisible(true);

        resendAckTimeoutRef.current = setTimeout(() => {
            setSubmitLabelVisible(false);

            resendAckTimeoutRef.current = setTimeout(() => {
                setSubmitLabelPhase('login');
                setSubmitLabelVisible(true);
                resendAckTimeoutRef.current = null;
            }, SUBMIT_LABEL_FADE_MS);
        }, RESEND_ACK_DISPLAY_MS);
    }, [clearResendAckTimeout]);

    useEffect(
        () => () => {
            clearResendAckTimeout();
        },
        [clearResendAckTimeout],
    );

    useEffect(() => {
        if (!autoSend || !normalizedEmail || requestSent) {
            return;
        }

        if (autoSendInitiatedRef.current) {
            return;
        }

        if (autoSendInFlightByEmail.has(normalizedEmail)) {
            autoSendInitiatedRef.current = true;
            awaitingRequestResponseRef.current = true;
            return;
        }

        autoSendInitiatedRef.current = true;
        sendCode();
        // Intentionally omit requestFetcher.state — re-running when the fetcher
        // returns to idle caused a second send before requestSent was set.
    }, [autoSend, normalizedEmail, requestSent, sendCode]);

    /**
     * Release the in-flight latch when this step goes away.
     *
     * The latch is keyed by email at module level so a Strict Mode remount
     * cannot double-send. It was cleared only when a response arrived — so a
     * reader who left the step mid-send took the entry with them, and coming
     * back hit the early return at the top of the auto-send effect: the
     * remounted component has a fresh fetcher with no data, so nothing was left
     * that could clear the latch or set `requestSent`, and the step opened
     * unable to send.
     */
    useEffect(
        () => () => {
            // Only if this instance is the one still waiting on the response.
            // The latch is module-level and keyed by email alone, so deleting
            // it unconditionally freed a *different* mounted step's guard and
            // let it send a second code — the double-send the latch exists to
            // stop. An instance that already saw its response has nothing to
            // release.
            if (!awaitingRequestResponseRef.current) return;
            autoSendInFlightByEmail.delete(normalizedEmail);
        },
        [normalizedEmail],
    );

    useEffect(() => {
        if (
            requestFetcher.state !== 'idle' ||
            !requestFetcher.data ||
            !awaitingRequestResponseRef.current
        ) {
            return;
        }

        const responseKey = `${requestFetcher.state}:${JSON.stringify(requestFetcher.data)}`;
        if (handledRequestFetcherKeyRef.current === responseKey) {
            return;
        }
        handledRequestFetcherKeyRef.current = responseKey;

        if (requestFetcher.data.ok) {
            awaitingRequestResponseRef.current = false;
            autoSendInFlightByEmail.delete(normalizedEmail);
            setResendSending(false);
            setRequestError(null);
            form.clearErrors('code');
            setRequestSent(true);
            setResendAvailableAt(Date.now() + RESEND_COOLDOWN_SECONDS * 1000);
            showResendAckOnSubmitButton();
            return;
        }

        awaitingRequestResponseRef.current = false;
        autoSendInFlightByEmail.delete(normalizedEmail);
        setResendSending(false);
        clearResendAckTimeout();
        setSubmitLabelPhase('login');
        setSubmitLabelVisible(true);
        const errorMessage =
            requestFetcher.data.message ??
            'Could not send the code. Try again in a moment.';
        setRequestError(errorMessage);
        if (requestSent) {
            form.setError('code', { message: errorMessage });
        }
    }, [
        clearResendAckTimeout,
        form,
        requestFetcher.data,
        requestFetcher.state,
        requestSent,
        normalizedEmail,
        showResendAckOnSubmitButton,
    ]);

    useEffect(() => {
        if (verifyFetcher.state !== 'idle' || !verifyFetcher.data) {
            return;
        }

        const responseKey = `${verifyFetcher.state}:${JSON.stringify(verifyFetcher.data)}`;
        if (handledVerifyFetcherKeyRef.current === responseKey) {
            return;
        }
        handledVerifyFetcherKeyRef.current = responseKey;

        if (verifyFetcher.data.ok) {
            onSuccess(verifyFetcher.data.trackedGuestID);
            return;
        }

        form.setError('code', {
            message:
                verifyFetcher.data.message ??
                'This code is invalid or has expired.',
        });
    }, [verifyFetcher.data, verifyFetcher.state, form, onSuccess]);

    const handleVerify = form.handleSubmit((data) => {
        handledVerifyFetcherKeyRef.current = null;
        form.clearErrors('code');
        verifyFetcher.submit(
            { email: normalizedEmail, code: data.code },
            { method: 'post', action: VERIFY_EMAIL_LOGIN_CODE_API_ROUTE },
        );
    });

    if (variant === 'rocco') {
        const codeValue = form.watch('code') ?? '';
        const codeError = form.formState.errors.code?.message;
        const verifying = verifyFetcher.state !== 'idle';

        return (
            <Form {...form}>
                <div className="flex flex-col">
                    {!requestSent && requestError && (
                        <p
                            role="alert"
                            className="mb-6 text-center text-sm text-destructive"
                        >
                            {requestError}
                        </p>
                    )}
                    <form
                        onSubmit={handleVerify}
                        // The large leading gap centres the step in the fixed
                        // desktop pane. A sheet takes the height of its step, so
                        // on a phone this is only a gap above the one thing the
                        // reader is here to do.
                        className={cn(
                            'flex flex-col',
                            isMobile ? 'mt-8' : 'mt-23.75',
                        )}
                    >
                        <OtpInput
                            value={codeValue}
                            onChange={(next) => {
                                form.clearErrors('code');
                                form.setValue('code', next);
                            }}
                            // Live once a send has either landed or visibly
                            // failed — not once it has SUCCEEDED. Gating on
                            // `requestSent` alone greyed the boxes out for
                            // someone holding a perfectly good code after a
                            // rate-limited send; gating on `sendingCode` alone
                            // made them live before any code had been asked
                            // for, so a reader could type six digits and be
                            // told they were wrong. A bad code is the server's
                            // to reject; a code that does not exist yet is not.
                            disabled={
                                sendingCode ||
                                verifying ||
                                (!requestSent && !requestError)
                            }
                            hasError={!!codeError}
                        />
                        {codeError && (
                            <p
                                role="alert"
                                className="mt-3 text-center text-sm text-destructive"
                            >
                                {codeError}
                            </p>
                        )}
                        <p className="mt-4.5 text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]">
                            {resendRemaining > 0 ? (
                                `Resend code in ${formatCountdown(resendRemaining)}`
                            ) : (
                                <button
                                    type="button"
                                    className="underline"
                                    onClick={handleResendCode}
                                    disabled={sendingCode || resendSending}
                                >
                                    Resend code
                                </button>
                            )}
                        </p>
                        {/* "Wrong email?" sits inside the form so the pair can
                            be pinned together — it is the escape hatch from the
                            action above it, and a sticky footer can only hold
                            what follows it in the flow. */}
                        <GateStickyFooter
                            className={isMobile ? 'mt-8' : 'mt-23'}
                        >
                            <RoccoButton
                                type="submit"
                                color="dark"
                                size="larger"
                                className="h-14 w-full rounded-full text-[1.125rem] font-medium"
                                isLoading={verifying || resendSending}
                                disabled={
                                    sendingCode ||
                                    (!requestSent && !requestError)
                                }
                            >
                                Verify &amp; Continue
                            </RoccoButton>
                            {!hideBack && (
                                <p className="mt-5 text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]">
                                    Wrong email?{' '}
                                    <button
                                        type="button"
                                        className="underline"
                                        onClick={onBack}
                                    >
                                        Go back
                                    </button>
                                </p>
                            )}
                        </GateStickyFooter>
                    </form>
                </div>
            </Form>
        );
    }

    return (
        <Form {...form}>
            <div className="mt-4 flex flex-col gap-4">
                <p className="text-neutral-600 text-center text-sm">
                    {requestSent
                        ? `We've sent a code to ${normalizedEmail}. Check your inbox and enter it below, or tap the link in the email.`
                        : autoSend
                          ? `Sending a one-time sign-in code to ${normalizedEmail}…`
                          : `Send a one-time sign-in code to ${normalizedEmail}.`}
                </p>

                {!requestSent ? (
                    autoSend && !requestError ? (
                        <LoginModalSubmitButton
                            type="button"
                            isLoading={sendingCode}
                            disabled
                        >
                            Sending code…
                        </LoginModalSubmitButton>
                    ) : (
                        <LoginModalSubmitButton
                            type="button"
                            isLoading={sendingCode}
                            onClick={sendCode}
                        >
                            Email me a code
                        </LoginModalSubmitButton>
                    )
                ) : (
                    <form
                        onSubmit={handleVerify}
                        className="flex flex-col gap-4"
                    >
                        <FormField
                            control={form.control}
                            name="code"
                            render={({ field, fieldState: { error } }) => (
                                <FormItem>
                                    <FormLabel>Sign-in code</FormLabel>
                                    <FormControl>
                                        <RoccoInput
                                            {...field}
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            placeholder="123-456"
                                        />
                                    </FormControl>
                                    <FormMessage>{error?.message}</FormMessage>
                                </FormItem>
                            )}
                        />
                        <LoginModalSubmitButton
                            type="submit"
                            isLoading={
                                verifyFetcher.state !== 'idle' || resendSending
                            }
                            labelVisible={submitLabelVisible}
                        >
                            {submitLabelPhase === 'code-sent' ? (
                                <>
                                    Code sent
                                    <CodeSentCheckIcon />
                                </>
                            ) : (
                                submitLabel
                            )}
                        </LoginModalSubmitButton>
                        <button
                            type="button"
                            className="text-neutral-600 text-sm underline"
                            onClick={handleResendCode}
                            disabled={sendingCode || resendSending}
                        >
                            Resend code
                        </button>
                    </form>
                )}

                {requestError && !requestSent ? (
                    <p className="text-sm text-red-600">{requestError}</p>
                ) : null}

                {!hideBack ? (
                    <button
                        type="button"
                        className="text-neutral-600 text-sm underline"
                        onClick={onBack}
                    >
                        Back
                    </button>
                ) : null}
            </div>
        </Form>
    );
}
